import { useCallback, useEffect, useState } from 'react';
import { useNavigate } from '@tanstack/react-router';
import { useTranslation } from 'react-i18next';
import { StoryViewer } from '@org/ui';
import { trpc } from '@org/clients/school';
import {
  canOpenStory,
  normalizeStoryLang,
  storyPosterStyleIndex,
  type StoryCandidate,
} from '../../lib/story';
import { useStoryLabels } from './use-story-labels';

interface StoryFlowUnit extends StoryCandidate {
  order: number;
  woman?: { name: string | null } | null;
}

interface ActiveStory {
  journeyId: string;
  unitId: string;
  mode: 'first' | 'reread';
}

/**
 * useStoryFlow — the journey wall's full-screen story viewer and `openStory`
 * for opening a unit's story (from the arrival banner's "meet her", or from a
 * poster's portrait). `activeUnitId` tells the wall which story is open, so
 * the arrival banner can carry on once the viewer closes.
 *
 * Safe to call before the wall has loaded (`units`/`journeyId` undefined).
 * When no unit has a published story this renders nothing and does nothing.
 */
export function useStoryFlow({
  journeyId,
  units,
}: {
  journeyId: string | undefined;
  units: readonly StoryFlowUnit[] | undefined;
}): {
  viewer: React.ReactNode;
  openStory: (unitId: string) => void;
  activeUnitId: string | null;
} {
  const { i18n } = useTranslation('p2e');
  const navigate = useNavigate();
  const labels = useStoryLabels();
  const utils = trpc.useUtils();

  const [active, setActive] = useState<ActiveStory | null>(null);

  const storyQuery = trpc.progress.getUnitStory.useQuery(
    { journeyId: active?.journeyId ?? '', unitId: active?.unitId ?? '' },
    { enabled: !!active, retry: false, refetchOnWindowFocus: false },
  );
  const markSeenMutation = trpc.progress.markStorySeen.useMutation({
    onSuccess: () => {
      utils.progress.getStudentJourneyWall.invalidate();
    },
  });

  // The story could not be loaded (e.g. it was unpublished meanwhile): skip it
  // quietly. It is already in the session set, so the banner stays away; the
  // wall is refreshed so a ring that is no longer true disappears.
  const loadFailed = !!active && storyQuery.isError && !storyQuery.isFetching;
  useEffect(() => {
    if (!loadFailed) return;
    setActive(null);
    utils.progress.getStudentJourneyWall.invalidate();
  }, [loadFailed, utils]);

  const story =
    active && storyQuery.data && storyQuery.data.unit.unitId === active.unitId
      ? storyQuery.data
      : null;

  const openStory = useCallback(
    (unitId: string) => {
      if (!journeyId) return;
      const unit = units?.find((u) => u.unitId === unitId);
      if (!unit || !canOpenStory(unit)) return;
      setActive({
        journeyId,
        unitId,
        // Not seen yet → the full first meeting (with the reveal), which is
        // also what marks it as seen. Seen → straight to the first card.
        mode: unit.story?.seenAt ? 'reread' : 'first',
      });
    },
    [journeyId, units],
  );

  const wallIndex = active ? units?.findIndex((u) => u.unitId === active.unitId) ?? -1 : -1;

  const viewer =
    active && story ? (
      <StoryViewer
        key={`${active.unitId}:${active.mode}`}
        woman={story.woman}
        cards={story.cards}
        unit={{
          name: story.unit.name ?? '',
          order: story.unit.order,
          classTotal: story.unit.classTotal,
          firstClassName: story.unit.firstClass?.name ?? null,
        }}
        posterStyleIndex={storyPosterStyleIndex(Math.max(wallIndex, 0))}
        lang={normalizeStoryLang(i18n.language)}
        labels={labels}
        mode={active.mode}
        onClose={() => setActive(null)}
        onSeen={() => {
          // Fire-and-forget: the viewer stays open; the wall refreshes behind it.
          if (story.seenAt) return;
          markSeenMutation.mutate({
            journeyId: active.journeyId,
            unitId: active.unitId,
          });
        }}
        onStartClass={() => {
          const firstClass = story.unit.firstClass;
          setActive(null);
          if (!firstClass) return;
          navigate({
            to: '/classes/$classId/block/$blockIdx',
            params: { classId: firstClass.classId, blockIdx: '0' },
          });
        }}
        onViewMural={() => {
          setActive(null);
          navigate({ to: '/mural' });
        }}
      />
    ) : null;

  return { viewer, openStory, activeUnitId: active?.unitId ?? null };
}
